import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {PermissionsChecker} from '@client/permissions-checker'
import {Backend} from '@common/backend'
import {LinkButton} from '@components/common/base/buttons'
import {Help} from '@components/common/base/help'
import {Input} from '@components/common/base/input'
import {RegistrationsPopup} from '@components/dashboard/company-users/registrations-popup'
import {RegistrationsTable} from '@components/dashboard/company-users/registrations-table'
import {useSyncEvent} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {UserAccess} from '@extensions/user-access'
import {Panel} from '../common/panel'
import {CompanyInvitePopup} from '../company-invite-popup/company-invite-popup'
import {CompanyInviteRoleUserPopup} from '../company-invite-popup/role-users/company-invite-role-user-popup'
import {isAdmin} from '../dashboard-utils'
import {pendingCompaniesAdminStore} from './pending-companies-admin-store'
import {
  CompanyUsersContext,
  useCompanyUsersStore,
  UsersStore
} from './users-store'
import {UsersTable} from './users-table'

export const CompanyUsers = ({className}: {className?: string}) => {
  const [store] = useState(() => new UsersStore())
  return (
    <CompanyUsersContext.Provider value={store}>
      <CompanyUsersBase className={className} />
    </CompanyUsersContext.Provider>
  )
}

const CompanyUsersBase = observer(({className}: {className?: string}) => {
  const [state, store] = useCompanyUsersStore()

  useEffect(() => {
    store.calculateLists()
    void pendingCompaniesAdminStore.updatePendingRegistrations()
  }, [])
  useEffect(() => {
    store.calculateFiltered()
  }, [state.filterQuery])
  useSyncEvent('current', {debounceTime: 1000, filter: '*'}, () => {
    store.calculateLists()
    store.calculateFiltered()
  })

  const canSeePendingUsers =
    PermissionsChecker.isAdmin() && Backend.features.pendingRegistrations

  return (
    <Panel
      title={Lang.msg.coworkers}
      titlePrefix={<TwTitlePrefix>({store.allUsersCount})</TwTitlePrefix>}
      header={
        <div className="flex items-center justify-end w-full">
          <Help name="users" />
        </div>
      }
      className={className}
    >
      {canSeePendingUsers &&
        pendingCompaniesAdminStore.state.isRegistrationsPopupOpened && (
          <RegistrationsPopup />
        )}

      <TopButtons />
      <div className="mt-16">
        <Input
          placeholder={Lang.msg.search}
          icon="IconSearch"
          value={state.filterQuery}
          onChange={api => store.changeFilterQuery(api.text)}
          debounceMs={300}
        />
      </div>
      {canSeePendingUsers && <RegistrationsTable />}
      <UsersTable />
      <div className="h-8" />
    </Panel>
  )
})

const TopButtons = observer(() => {
  const [state, store] = useCompanyUsersStore()

  const supportSyncRoleUsers = globals.supportSyncRoleUsers
  const companyId = globals.getCompanyId()

  return (
    <TwTopButtons $roleUsers={supportSyncRoleUsers}>
      {state.isInvitePopupOpened && (
        <CompanyInvitePopup
          close={() => store.setInvitePopupOpen(false)}
          inviteFrom={'invite-employee'}
        />
      )}
      {supportSyncRoleUsers && state.isInviteRoleUserPopupOpened && (
        <CompanyInviteRoleUserPopup
          close={() => store.setInviteRoleUserPopupOpen(false)}
        />
      )}
      {UserAccess.canInviteUser(companyId) && (
        <LinkButton
          size="small"
          icon="IconUserPlus"
          onClick={() => store.setInvitePopupOpen(true)}
        >
          {Lang.msg.companyDashboard.addUser}
        </LinkButton>
      )}

      {supportSyncRoleUsers &&
        UserAccess.canInviteUser(companyId, !supportSyncRoleUsers) && (
          <LinkButton
            size="small"
            icon="IconUserPlus"
            onClick={() => store.setInviteRoleUserPopupOpen(true)}
          >
            {Lang.msg.companyDashboard.addVirtualUser}
          </LinkButton>
        )}
      <div className="flex gap-40 ml-auto">
        {isAdmin() && (
          <LinkButton
            size="small"
            icon="IconDownload"
            onClick={e => store.downloadPermissionsXlsx(e)}
          >
            {Lang.msg.users.downloadXlsxPermissions}
          </LinkButton>
        )}
        <LinkButton
          size="small"
          icon="IconDownload"
          onClick={e => store.downloadXlsx(e)}
        >
          {Lang.msg.users.downloadXlsxList}
        </LinkButton>
      </div>
    </TwTopButtons>
  )
})

const TwTopButtons = tw.div<{$roleUsers?: boolean}>`
  flex
  justify-start
  gap-16
  ${p => {
    if (p.$roleUsers) {
      return 'gap-16 min-w-848'
    }
    return ''
  }}
`

const TwTitlePrefix = tw.div`
  text-obscure
  font-medium
  text-16
  leading-none
  flex
  items-center
  ml-4
`
